import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  Signal
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import { toObservable } from '@angular/core/rxjs-interop';
import { catchError, filter, of, take } from 'rxjs';
import { Routing } from '../../app.routes';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';
import { LanguageStore } from '../../shared/services/language.store';
import { SnackBarService } from '../../shared/services/snack-bar.service';
import { StepperService } from '../../shared/services/stepper.service';
import {
  AuthEntity,
  AuthStateInterface,
  AuthStateService
} from '../../shared/services/state/auth-state.service';
import { SubscriptionFeatureService } from '../../shared/services/feature/subscription-feature.service';

@Component({
  selector: 'app-acs-reprise-projet',
  imports: [
    CommonModule,
    MatInputModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatCardModule,
    MatButtonModule,
    TranslatePipe
  ],
  templateUrl: './reprise-projet.component.html',
  styles: `
    @media only screen and (min-width: 1224px) {
      form {
        width: 418px;
        margin-left: 15%;
      }

      .full-height {
        height: 65vh;
      }
    }
  `,
  providers: [TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RepriseProjetComponent implements OnInit {
  public form: FormGroup = inject(FormBuilder).group({
    code: [
      null,
      [
        Validators.required,
        Validators.minLength(6),
        Validators.maxLength(6),
        Validators.pattern('[0-9]{6}')
      ]
    ]
  });
  readonly #activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  readonly authStateService: AuthStateService = inject(AuthStateService);
  readonly #stepperService: StepperService = inject(StepperService);
  readonly #snackBarService: SnackBarService = inject(SnackBarService);
  readonly #translatePipe = inject(TranslatePipe);
  readonly #languageStore = inject(LanguageStore);
  readonly #router: Router = inject(Router);
  readonly #subscriptionFeatureService = inject(SubscriptionFeatureService);
  private readonly langReady$ = toObservable(this.#languageStore.currentLanguage).pipe(
    filter(Boolean),
    take(1)
  );
  #uuid!: string;
  #producerCode!: string|null;
  #mode!: string|null;
  #entity!: string;

  public toastMessage: Signal<string> = computed(() => {
    this.#languageStore.currentLanguageCode();
    return this.#translatePipe.transform('recovery.toast');
  });

  /**
   * A l init du composant on verifie que les parametre de l url sont bon
   * Puis on envoye une requete de code de reprise projet ( envoye par email )
   */
  public ngOnInit(): void {
    const params = this.#activatedRoute.snapshot.data[
      'queryParams'
    ] as AuthStateInterface;
    if (params.uuid && params.entity) {
      this.#uuid = params.uuid;
      this.#entity = params.entity;
      this.#producerCode = params.producerCode;
      this.#mode = params.mode;
      this.langReady$.subscribe(() => this.#snackBarService.success(this.toastMessage()));
    }
  }

  private get codeCtrl(): FormControl {
    return this.form.get('code') as FormControl;
  }

  /**
   * Soumission du formulaire pour l envoie du code recu par email
   * Set les parametre d'url et redirige vers la page projet
   */
  public confirmCode(): void {
    if (this.#uuid) {
      this.authStateService
        .requestAccessToken(this.#uuid, this.codeCtrl.value)
        .pipe(
          catchError((err) => {
            this.#snackBarService.errorMessage(err.error.message);
            return of(null);
          })
        )
        .subscribe((res) => {
          if (res) {
            this.authStateService.setToken(res.data.token);
            this.authStateService.updateData({
              token: res.data.token,
              uuid: this.#uuid,
              producerCode: this.#producerCode,
              mode: this.#mode,
              entity: this.#entity as AuthEntity,
              token_producer: res.data.token_producer
            });
            this.#subscriptionFeatureService
              .getProjectInfo(this.#uuid, this.#producerCode, this.#mode)
              .pipe(
                catchError((err) => {
                  this.#snackBarService.errorMessage(err.error.message);
                  return of(null);
                })
              )
              .subscribe((project) => {
                if (project) {
                  this.#router
                    .navigate([Routing.PROJECT], {
                      queryParams: {
                        token: res.data.token,
                        uuid: this.#uuid,
                        producer_code: this.#producerCode,
                        mode: this.#mode,
                        entity: this.#entity,
                        token_producer: res.data.token_producer
                      }
                    })
                    .then();
                }
              });
          }
        });
    }
  }
}
